import ApiServiceBase from "@/services/api/apiServiceBase";
import {PostPrivacy} from "@/definitions/PostPrivacy";
import {FeedPostInterface} from "@/interfaces/FeedPostInterface";
import {SocialFeedType} from "@/definitions/SocialFeedType";
import {UserBasicInformationInterface} from "@/interfaces/UserBasicInformationInterface";
import {dataUrlToBlob, normalizePaginated} from "@/services/api/normalizeProfile";
import {normalizeFeedComment, normalizeFeedPost, normalizeFeedPosts} from "@/services/api/normalizeFeed";

export interface FeedServiceOptions {
    visibility?: PostPrivacy[];
}

/** Module-level page cursors because ApiService.Feed() returns a fresh instance per call. */
const feedPages: Record<string, number> = {};
let lastViewedFeedId: number | null = null;
const lastReactionByFeedId: Record<number, number> = {};
const lastReactionByCommentId: Record<number, number> = {};

function mapFeedFilter(type: SocialFeedType | string): string {
    switch (type) {
        case SocialFeedType.Following:
        case 'following':
        case 'followings':
            return 'followings';
        case SocialFeedType.Friends:
        case 'friends':
            return 'friends';
        case SocialFeedType.ForYou:
        case 'for-you':
        case 'for_you':
        default:
            return 'for_you';
    }
}

/** Map legacy emoji / thumbs to contract emojiName. */
function mapEmojiName(emoji?: string): 'hot' | 'devil' | 'naughty' | 'love' | 'fun' {
    const raw = String(emoji || 'love').toLowerCase();
    if (raw.includes('hot') || raw === '🔥') return 'hot';
    if (raw.includes('devil') || raw === '😈') return 'devil';
    if (raw.includes('naughty') || raw === '🌶️' || raw === '🌶') return 'naughty';
    if (raw.includes('fun') || raw === '😂' || raw === '😆') return 'fun';
    if (raw.includes('thumb') || raw === '👍' || raw.includes('love') || raw === '❤️' || raw === '❤') return 'love';
    if (['hot', 'devil', 'naughty', 'love', 'fun'].includes(raw)) {
        return raw as 'hot' | 'devil' | 'naughty' | 'love' | 'fun';
    }
    return 'love';
}

function mapVisibility(visibility: PostPrivacy | string): string {
    const v = String(visibility);
    if (v === PostPrivacy.InnerCircle || v === 'INNER_CIRCLE') return 'INNER_CIRCLE';
    return 'PUBLIC';
}

export class FeedService extends ApiServiceBase {

    private async fetchFeedPage(filter: string, page: number, limit = 10): Promise<any[]> {
        const payload = await this.http?.get('/post_feeds', {filter, page, limit});
        return normalizeFeedPosts(normalizePaginated(payload).data);
    }

    public async getBefore(type: SocialFeedType = SocialFeedType.ForYou): Promise<any> {
        const filter = mapFeedFilter(type);
        feedPages[filter] = 1;
        return this.fetchFeedPage(filter, 1);
    }

    public async getAfter(postId?: number, type: SocialFeedType = SocialFeedType.ForYou): Promise<any> {
        const filter = mapFeedFilter(type);
        const next = (feedPages[filter] || 1) + 1;
        feedPages[filter] = next;
        return this.fetchFeedPage(filter, next);
    }

    public async getPost(postId: number): Promise<FeedPostInterface> {
        lastViewedFeedId = postId;
        const post = normalizeFeedPost(await this.http?.get(`/feeds/${postId}`));
        return post;
    }

    public async post(
        content: string,
        visibility: PostPrivacy,
        extras: {
            allowComments?: boolean;
            isNsfw?: boolean;
            mentions?: number[];
            eventId?: number;
            media?: Array<string | Blob | File>;
        } = {},
    ): Promise<FeedPostInterface> {
        const form = new FormData();
        form.append('content', content ?? '');
        form.append('visibility', mapVisibility(visibility));
        form.append('allow_comments', String(extras.allowComments !== false));
        form.append('isNsfw', String(Boolean(extras.isNsfw || visibility === PostPrivacy.XRated)));
        if (extras.mentions?.length) {
            form.append('mentions', JSON.stringify(extras.mentions));
        }
        if (extras.eventId != null) {
            form.append('eventId', String(extras.eventId));
        }
        if (extras.media?.length) {
            for (let i = 0; i < extras.media.length; i++) {
                const item = extras.media[i];
                if (typeof item === 'string') {
                    const blob = await dataUrlToBlob(item);
                    form.append('media', blob, `media-${i}.jpg`);
                } else {
                    form.append('media', item, (item as File).name || `media-${i}`);
                }
            }
        }
        return normalizeFeedPost(await this.http?.upload('/feeds', form));
    }

    public async attachMedia(postId: number, media: string[]): Promise<FeedPostInterface> {
        const form = new FormData();
        for (let i = 0; i < media.length; i++) {
            const blob = await dataUrlToBlob(media[i]);
            form.append('media', blob, `media-${i}.jpg`);
        }
        return normalizeFeedPost(await this.http?.upload(`/feeds/${postId}/upload-media`, form));
    }

    public async attachVideo(postId: number, onProgress: any, thumbnail?: string, video?: any): Promise<any> {
        const form = new FormData();
        if (thumbnail) {
            const thumbBlob = await dataUrlToBlob(thumbnail);
            form.append('media', thumbBlob, 'thumbnail.jpg');
        }
        if (video) {
            form.append('media', video, (video as File).name || 'video.mp4');
        }
        return this.http?.upload(`/feeds/${postId}/upload-media`, form, (progress: any) => {
            onProgress?.(progress);
        });
    }

    public async allowComments(postId: number, bool: boolean): Promise<FeedPostInterface> {
        const form = new FormData();
        form.append('allow_comments', String(bool));
        return normalizeFeedPost(await this.http?.upload(`/feeds/${postId}`, form, undefined, 'PUT'));
    }

    public async updatePost(postId: number, content: string, visibility: PostPrivacy): Promise<FeedPostInterface> {
        const form = new FormData();
        form.append('content', content ?? '');
        form.append('visibility', mapVisibility(visibility));
        form.append('isNsfw', String(visibility === PostPrivacy.XRated));
        return normalizeFeedPost(await this.http?.upload(`/feeds/${postId}`, form, undefined, 'PUT'));
    }

    public async reportPost(postId: number, reason = 'Inappropriate content'): Promise<any> {
        return this.http?.post('/reports/feeds', {feedId: Number(postId), reason});
    }

    public async reportPostComment(commentId: number, reason = 'Inappropriate content'): Promise<any> {
        return this.http?.post('/reports/comments', {commentId: Number(commentId), reason});
    }

    public async deletePost(postId: number): Promise<any> {
        return this.http?.delete('/feeds', {feedId: Number(postId)});
    }

    public async postReaction(postId: number, emoji?: string): Promise<any> {
        const result = await this.http?.post('/feeds/react', {
            feedId: Number(postId),
            emojiName: mapEmojiName(emoji),
        });
        const reactionId = result?.id ?? result?.reactionId ?? result?.reaction?.id;
        if (reactionId != null) {
            lastReactionByFeedId[postId] = Number(reactionId);
        }
        return result;
    }

    public async deletePostReaction(postId: number, reactionId?: number): Promise<any> {
        const id = reactionId ?? lastReactionByFeedId[postId];
        if (id == null) {
            return Promise.reject({
                success: false,
                message: 'Reaction id required to unreact',
                error: 'REACTION_ID_REQUIRED',
            });
        }
        delete lastReactionByFeedId[postId];
        return this.http?.delete(`/reactions/${id}`, {});
    }

    public async deletePostComment(commentId: number): Promise<any> {
        return this.http?.delete('/feeds/comment', {commentId: Number(commentId)});
    }

    public async postCommentReaction(commentId: number): Promise<any> {
        const result = await this.http?.post('/feeds/comment/react', {commentId: Number(commentId)});
        const reactionId = result?.id ?? result?.reactionId ?? result?.reaction?.id;
        if (reactionId != null) {
            lastReactionByCommentId[commentId] = Number(reactionId);
        }
        return result;
    }

    public async deletePostCommentReaction(commentId: number, reactionId?: number): Promise<any> {
        const id = reactionId ?? lastReactionByCommentId[commentId];
        if (id == null) {
            return Promise.reject({
                success: false,
                message: 'Reaction id required to unreact comment',
                error: 'REACTION_ID_REQUIRED',
            });
        }
        delete lastReactionByCommentId[commentId];
        return this.http?.delete(`/feeds/comment/react/${id}`, {});
    }

    /**
     * Top-level: pass feed id (views use post.comment_id aliased to post.id).
     * Reply: pass parent comment id (uses last viewed feed id from getPost).
     */
    public async postCommentReply(commentOrFeedId: number, comment: string): Promise<FeedPostInterface> {
        const form = new FormData();
        form.append('comment', comment);
        if (lastViewedFeedId && Number(commentOrFeedId) !== Number(lastViewedFeedId)) {
            form.append('feedId', String(lastViewedFeedId));
            form.append('parentId', String(commentOrFeedId));
        } else {
            form.append('feedId', String(commentOrFeedId));
        }
        return normalizeFeedComment(await this.http?.upload('/feeds/comment', form));
    }

    public async comment(feedId: number, comment: string, parentId?: number, media?: Array<Blob | File>): Promise<any> {
        const form = new FormData();
        form.append('feedId', String(feedId));
        form.append('comment', comment);
        if (parentId != null) form.append('parentId', String(parentId));
        if (media?.length) {
            for (let i = 0; i < media.length; i++) {
                form.append('media', media[i], (media[i] as File).name || `media-${i}`);
            }
        }
        return normalizeFeedComment(await this.http?.upload('/feeds/comment', form));
    }

    public async updateComment(_commentId: number, _comment: string): Promise<FeedPostInterface> {
        return Promise.reject({
            success: false,
            message: 'Updating comments is not supported',
            error: 'NOT_SUPPORTED',
        });
    }

    public async commentReactions(commentId: number): Promise<any> {
        // No dedicated list route for comment reactors; return empty for UI lists.
        void commentId;
        return [];
    }

    public async reveal(feedId: number): Promise<any> {
        return this.http?.post('/feeds/reveal', {feedId: Number(feedId)});
    }

    public async revealComment(commentId: number): Promise<any> {
        return this.http?.post('/feeds/comments/reveal', {commentId: Number(commentId)});
    }

    public async repost(feedId: number, repostText?: string, mentions?: number[]): Promise<any> {
        const body: Record<string, any> = {feedId: Number(feedId)};
        if (repostText) body.repost_text = repostText;
        if (mentions?.length) body.mentions = mentions;
        return this.http?.post('/feeds/repost', body);
    }

    public async trendingTags(limit = 10, days = 7): Promise<any> {
        return this.http?.get('/feeds/trending-tags', {limit, days});
    }

    public async byTags(tagQuery: string, page = 1, limit = 10, tagIds?: number[]): Promise<any> {
        const body: Record<string, any> = {tagQuery, page, limit};
        if (tagIds?.length) body.tagIds = tagIds;
        const payload = await this.http?.post('/feeds/by-tags', body);
        const result = normalizePaginated(payload);
        return {...result, data: normalizeFeedPosts(result.data)};
    }

    public async nearby(radius = 20, type?: string, page = 1): Promise<UserBasicInformationInterface[]> {
        // Nearby discovery lives on /users with lat/lng; without coords return empty.
        void radius;
        void type;
        void page;
        return [];
    }

    public async getReactions(feedId: number, page = 1, limit = 20): Promise<any> {
        return normalizePaginated(await this.http?.get(`/feeds/${feedId}/reactions`, {page, limit}));
    }
}
